import { type IntlShape, defineMessages } from 'react-intl';

const MINUTE = 60000;
const HOUR = 3600000;
const DAY = 86400000;
const WEEK = 604800000;
const MONTH = 2592000000;
const YEAR = 31536000000;

export const timeFormatter: Intl.DateTimeFormat = new Intl.DateTimeFormat('en-US', {
	hour: 'numeric',
	minute: '2-digit',
});

export const dateFormatter: Intl.DateTimeFormat = new Intl.DateTimeFormat('en-US', {
	year: 'numeric',
	month: 'short',
	day: 'numeric',
});

type RelativeTimeMessageKey = keyof typeof relativeTimeMessages;

export interface RelativeTimeBucket {
	maxDiffMs: number;
	message: RelativeTimeMessageKey;
	valueName?: string;
	unitMs?: number;
	countCalculator?: (diffMs: number) => number;
}

export interface RelativeTimeConfig {
	buckets: RelativeTimeBucket[];
	specialCase?: (params: {
		timestampMs: number;
		nowMs: number;
		diffMs: number;
		intl: IntlShape;
	}) => string | null;
	fallback?: (params: {
		timestampMs: number;
		nowMs: number;
		diffMs: number;
		intl: IntlShape;
	}) => string;
}

export const relativeTimeMessages: {
	justNow: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	minutesAgo: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	hoursAgo: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	daysAgo: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	weeksAgo: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	monthsAgo: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	yearsAgo: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	justAMomentAgo: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	lessThanAMinuteAgo: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	aMinuteAgo: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	contentReportMinutesAgo: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	aboutAnHourAgo: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	contentReportHoursAgo: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	yesterdayAt: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	justNow: {
		id: 'macro-utils.relative-time-just-now',
		defaultMessage: 'just now',
		description: 'Relative time label shown when the activity happened less than a minute ago.',
	},
	minutesAgo: {
		id: 'macro-utils.relative-time-minutes-ago',
		defaultMessage:
			'{minuteCount, plural, one {{minuteCount} minute ago} other {{minuteCount} minutes ago}}',
		description: 'Relative time label for minutes ago. {minuteCount} is the number of minutes.',
	},
	hoursAgo: {
		id: 'macro-utils.relative-time-hours-ago',
		defaultMessage: '{hourCount, plural, one {{hourCount} hour ago} other {{hourCount} hours ago}}',
		description: 'Relative time label for hours ago. {hourCount} is the number of hours.',
	},
	daysAgo: {
		id: 'macro-utils.relative-time-days-ago',
		defaultMessage: '{dayCount, plural, one {{dayCount} day ago} other {{dayCount} days ago}}',
		description: 'Relative time label for days ago. {dayCount} is the number of days.',
	},
	weeksAgo: {
		id: 'macro-utils.relative-time-weeks-ago',
		defaultMessage: '{weekCount, plural, one {{weekCount} week ago} other {{weekCount} weeks ago}}',
		description: 'Relative time label for weeks ago. {weekCount} is the number of weeks.',
	},
	monthsAgo: {
		id: 'macro-utils.relative-time-months-ago',
		defaultMessage:
			'{monthCount, plural, one {{monthCount} month ago} other {{monthCount} months ago}}',
		description: 'Relative time label for months ago. {monthCount} is the number of months.',
	},
	yearsAgo: {
		id: 'macro-utils.relative-time-years-ago',
		defaultMessage: '{yearCount, plural, one {{yearCount} year ago} other {{yearCount} years ago}}',
		description: 'Relative time label for years ago. {yearCount} is the number of years.',
	},
	justAMomentAgo: {
		id: 'macro-utils.relative-time-just-a-moment-ago',
		defaultMessage: 'just a moment ago',
		description: 'Relative time label shown when the activity happened less than 4 seconds ago.',
	},
	lessThanAMinuteAgo: {
		id: 'macro-utils.relative-time-less-than-a-minute-ago',
		defaultMessage: 'less than a minute ago',
		description: 'Relative time label shown when the activity happened less than a minute ago.',
	},
	aMinuteAgo: {
		id: 'macro-utils.relative-time-a-minute-ago',
		defaultMessage: 'a minute ago',
		description: 'Relative time label shown when the activity happened about a minute ago.',
	},
	contentReportMinutesAgo: {
		id: 'macro-utils.content-report-minutes-ago',
		defaultMessage:
			'{minuteCount, plural, one {{minuteCount} minute ago} other {{minuteCount} minutes ago}}',
		description:
			'Relative time label for content report minutes ago. {minuteCount} is the number of minutes.',
	},
	aboutAnHourAgo: {
		id: 'macro-utils.relative-time-about-an-hour-ago',
		defaultMessage: 'about an hour ago',
		description: 'Relative time label shown when the activity happened about an hour ago.',
	},
	contentReportHoursAgo: {
		id: 'macro-utils.content-report-hours-ago',
		defaultMessage:
			'{hourCount, plural, one {about {hourCount} hour ago} other {about {hourCount} hours ago}}',
		description:
			'Relative time label for content report hours ago. {hourCount} is the number of hours.',
	},
	yesterdayAt: {
		id: 'macro-utils.relative-time-yesterday-at',
		defaultMessage: 'yesterday at {time}',
		description:
			'Relative time label for yesterday with a specific time. {time} is the formatted time.',
	},
});

const formatBucketMessage = (
	bucket: RelativeTimeBucket,
	diffMs: number,
	intl: IntlShape,
): string => {
	if (bucket.valueName && bucket.unitMs) {
		const count = bucket.countCalculator
			? bucket.countCalculator(diffMs)
			: Math.floor(diffMs / bucket.unitMs);
		return intl.formatMessage(relativeTimeMessages[bucket.message], {
			[bucket.valueName]: count,
		});
	}

	return intl.formatMessage(relativeTimeMessages[bucket.message]);
};

export const defaultRelativeTimeConfig: RelativeTimeConfig = {
	buckets: [
		{ maxDiffMs: MINUTE, message: 'justNow' },
		{
			maxDiffMs: HOUR,
			message: 'minutesAgo',
			valueName: 'minuteCount',
			unitMs: MINUTE,
		},
		{
			maxDiffMs: DAY,
			message: 'hoursAgo',
			valueName: 'hourCount',
			unitMs: HOUR,
		},
		{
			maxDiffMs: WEEK,
			message: 'daysAgo',
			valueName: 'dayCount',
			unitMs: DAY,
		},
		{
			maxDiffMs: MONTH,
			message: 'weeksAgo',
			valueName: 'weekCount',
			unitMs: WEEK,
		},
		{
			maxDiffMs: YEAR,
			message: 'monthsAgo',
			valueName: 'monthCount',
			unitMs: MONTH,
		},
		{
			maxDiffMs: Number.POSITIVE_INFINITY,
			message: 'yearsAgo',
			valueName: 'yearCount',
			unitMs: YEAR,
		},
	],
};

export const contentReportRelativeTimeConfig: RelativeTimeConfig = {
	buckets: [
		{ maxDiffMs: 4 * 1000, message: 'justAMomentAgo' },
		{ maxDiffMs: MINUTE, message: 'lessThanAMinuteAgo' },
		{ maxDiffMs: 2 * MINUTE, message: 'aMinuteAgo' },
		{
			maxDiffMs: 50 * MINUTE,
			message: 'contentReportMinutesAgo',
			valueName: 'minuteCount',
			unitMs: MINUTE,
			countCalculator: (diffMs) => Math.round(diffMs / MINUTE),
		},
		{ maxDiffMs: 90 * MINUTE, message: 'aboutAnHourAgo' },
		{
			maxDiffMs: DAY,
			message: 'contentReportHoursAgo',
			valueName: 'hourCount',
			unitMs: HOUR,
			countCalculator: (diffMs) => Math.round(diffMs / HOUR),
		},
	],
	specialCase: ({ timestampMs, nowMs, diffMs, intl }) => {
		const savedTime = new Date(timestampMs);
		const now = new Date(nowMs);

		if (isYesterday(savedTime, now) && diffMs > 5 * HOUR) {
			return formatYesterdayTime(savedTime, intl);
		}

		return null;
	},
	fallback: ({ timestampMs }) => formatAbsoluteDate(new Date(timestampMs)),
};

export const getRelativeTimeWithConfig = (
	timestamp: number,
	intl: IntlShape,
	config: RelativeTimeConfig,
): string => {
	const nowMs = Date.now();
	const diffMs = nowMs - timestamp;

	if (config.specialCase) {
		const specialResult = config.specialCase({
			timestampMs: timestamp,
			nowMs,
			diffMs,
			intl,
		});

		if (specialResult !== null) {
			return specialResult;
		}
	}

	for (const bucket of config.buckets) {
		if (diffMs < bucket.maxDiffMs) {
			return formatBucketMessage(bucket, diffMs, intl);
		}
	}

	if (config.fallback) {
		return config.fallback({ timestampMs: timestamp, nowMs, diffMs, intl });
	}

	return intl.formatMessage(relativeTimeMessages.justNow);
};

export const getRelativeTime = (timestamp: number, intl: IntlShape): string => {
	const now = Date.now();
	const diff = now - timestamp;

	if (diff < MINUTE) {
		return intl.formatMessage(relativeTimeMessages.justNow);
	} else if (diff < HOUR) {
		return intl.formatMessage(relativeTimeMessages.minutesAgo, {
			minuteCount: Math.floor(diff / MINUTE),
		});
	} else if (diff < DAY) {
		return intl.formatMessage(relativeTimeMessages.hoursAgo, {
			hourCount: Math.floor(diff / HOUR),
		});
	} else if (diff < WEEK) {
		return intl.formatMessage(relativeTimeMessages.daysAgo, {
			dayCount: Math.floor(diff / DAY),
		});
	} else if (diff < MONTH) {
		return intl.formatMessage(relativeTimeMessages.weeksAgo, {
			weekCount: Math.floor(diff / WEEK),
		});
	} else if (diff < YEAR) {
		return intl.formatMessage(relativeTimeMessages.monthsAgo, {
			monthCount: Math.floor(diff / MONTH),
		});
	} else {
		return intl.formatMessage(relativeTimeMessages.yearsAgo, {
			yearCount: Math.floor(diff / YEAR),
		});
	}
};

export const isYesterday = (date: Date, now: Date): boolean => {
	const yesterday = new Date(now);
	yesterday.setDate(now.getDate() - 1);
	return yesterday.toLocaleDateString() === date.toLocaleDateString();
};

export const formatYesterdayTime = (date: Date, intl: IntlShape): string => {
	return intl.formatMessage(relativeTimeMessages.yesterdayAt, {
		time: timeFormatter.format(date),
	});
};

export const formatAbsoluteDate = (date: Date): string => {
	return dateFormatter.format(date);
};

export const formatContentReportEpochTime = (timestamp: number, intl: IntlShape): string => {
	return getRelativeTimeWithConfig(timestamp, intl, contentReportRelativeTimeConfig);
};
